import TimeExtent from '@arcgis/core/TimeExtent';
import TimeSlider from '@arcgis/core/widgets/TimeSlider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Box } from '@rsos/base-components';
import { selectActiveLayers } from '@rsos/reduxDataStore';
import type {
  TimeSliderComponentProps,
  GISTimeLayer,
  TimeLayer,
} from './TimeSlider.types';
import { TimeSliderContainer } from './TimeSliderContainer';

const TimeSliderComponent = ({ map }: TimeSliderComponentProps) => {
  const timeSliderRef = useRef<TimeSlider | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);

  const activeLayers = useSelector(selectActiveLayers) as TimeLayer[];

  const [timeWindow, setTimeWindow] = useState<TimeExtent | null>(null);
  const [fullTimeExtent, setFullTimeExtent] = useState<TimeExtent | null>(null);

  // Filter layers to only include those with time information
  const activeTimeEnabledLayers = useMemo(() => {
    return activeLayers.filter(layer => {
      return layer.gisLayer.timeInfo && layer.gisLayer.timeInfo.fullTimeExtent;
    });
  }, [activeLayers]);

  /**
   * Manages container lifecycle and layer selection
   * - Destroys existing TimeSlider widget and removes container when layers change
   * - Creates new DOM container for map UI integration
   * - Auto-selects appropriate layer when selection becomes invalid
   */
  useEffect(() => {
    if (!map?.mapInstance?.view?.ui) return;

    if (timeSliderRef.current && timeSliderRef.current.destroy) {
      timeSliderRef.current.destroy();
    }

    if (mapContainerRef.current && map.mapInstance.view.ui) {
      map.mapInstance.view.ui.remove(mapContainerRef.current);
      mapContainerRef.current = null;
    }

    if (activeTimeEnabledLayers.length === 0) {
      setSelectedLayerId(null);
      return;
    }

    // Use createElement instead of React component to avoid lifecycle conflicts with ArcGIS map UI
    const container = document.createElement('div');
    mapContainerRef.current = container;
    map.mapInstance.view.ui.add(mapContainerRef.current, 'bottom-right');

    // Auto-select first layer if none selected, or switch if current selection is invalid
    if (!selectedLayerId && activeTimeEnabledLayers.length > 0) {
      setSelectedLayerId(activeTimeEnabledLayers[0].id);
    } else if (
      selectedLayerId &&
      !activeTimeEnabledLayers.some(layer => layer.id === selectedLayerId)
    ) {
      setSelectedLayerId(activeTimeEnabledLayers[0].id);
    }
  }, [map, activeTimeEnabledLayers, selectedLayerId]);

  const selectedLayer = activeTimeEnabledLayers.find(
    layer => layer.id === selectedLayerId,
  );

  // Extract time extent information from the selected map layer
  useEffect(() => {
    if (!map?.mapInstance?.view) return;

    if (!selectedLayerId || !selectedLayer) return;

    map.mapInstance.view.when(() => {
      const mapLayer = selectedLayer.gisLayer as GISTimeLayer;
      if (mapLayer?.timeInfo?.fullTimeExtent) {
        const startDate = new Date(mapLayer.timeInfo.fullTimeExtent.start);
        const endDate = new Date(mapLayer.timeInfo.fullTimeExtent.end);
        const newTimeExtent = new TimeExtent({
          start: startDate,
          end: endDate,
        });

        setFullTimeExtent(newTimeExtent);
        setTimeWindow(newTimeExtent);
      }
    });
  }, [
    selectedLayerId,
    activeTimeEnabledLayers,
    map.mapInstance?.view,
    selectedLayer,
  ]);

  // Create ArcGIS TimeSlider widget when time extent data is available
  useEffect(() => {
    if (
      !map?.mapInstance?.view ||
      !fullTimeExtent ||
      !timeWindow ||
      !contentRef.current ||
      activeTimeEnabledLayers.length === 0
    )
      return;

    try {
      const timeSlider = new TimeSlider({
        container: contentRef.current,
        view: map.mapInstance.view,
        mode: 'time-window',
        timeVisible: true,
        loop: false,
        playRate: 1000,
        fullTimeExtent,
        timeExtent: timeWindow,
        visible: true,
      });

      timeSliderRef.current = timeSlider;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [
    fullTimeExtent,
    timeWindow,
    activeTimeEnabledLayers,
    map.mapInstance?.view,
  ]);

  useEffect(() => {
    return () => {
      if (timeSliderRef.current?.destroy) {
        timeSliderRef.current.destroy();
        timeSliderRef.current = null;
      }

      if (mapContainerRef.current && map?.mapInstance?.view?.ui) {
        try {
          map.mapInstance.view.ui.remove(mapContainerRef.current);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
        mapContainerRef.current = null;
      }
    };
  }, [map]);

  if (activeTimeEnabledLayers.length === 0 || !mapContainerRef.current) {
    return null;
  }

  return createPortal(
    <TimeSliderContainer
      timeEnabledLayers={activeTimeEnabledLayers}
      isExpanded={isExpanded}
      selectedLayerId={selectedLayerId}
      onLayerChange={setSelectedLayerId}
      onToggle={() => setIsExpanded(prev => !prev)}
    >
      <Box ref={contentRef} />
    </TimeSliderContainer>,
    mapContainerRef.current,
  );
};

export default TimeSliderComponent;
